import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ALLOWED_EXTENSIONS } from "@wedding/shared-types";
import { uploadSingleFile } from "../api/events.js";

export type QueueItemStatus = "pending" | "uploading" | "done" | "error";

export interface QueueItem {
  id: string;
  file: File;
  status: QueueItemStatus;
  progress: number;
  error?: string;
  mediaId?: string;
}

interface QueuedFile {
  id: string;
  file: File;
}

const MAX_CONCURRENT_CLIENT_UPLOADS = 3;
const ALLOWED_EXTENSIONS_SET: ReadonlySet<string> = new Set(ALLOWED_EXTENSIONS);

function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot === -1 ? "" : filename.slice(dot + 1).toLowerCase();
}

function makeId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} Go`;
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} Mo`;
  return `${Math.round(bytes / 1024)} Ko`;
}

export function useUploadQueue(token: string, maxFileSizeBytes: number, maxFilesPerUpload: number) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const activeCount = useRef(0);
  const itemsRef = useRef<QueueItem[]>([]);
  itemsRef.current = items;

  // A plain array mutated synchronously via shift/push, carrying the actual
  // File alongside its id — not just an id to look up in `items` later.
  // React state (`items`) only reflects a just-added item on the *next*
  // render, so a same-tick call that tried to resolve "the file for this
  // id" via `itemsRef.current.find(...)` right after `addFiles` would find
  // nothing (stale ref) and silently drop the upload. Carrying the File
  // directly in the queue sidesteps that render-timing gap entirely.
  const pendingQueueRef = useRef<QueuedFile[]>([]);

  const updateItem = useCallback((id: string, patch: Partial<QueueItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const runNext = useCallback(() => {
    if (activeCount.current >= MAX_CONCURRENT_CLIENT_UPLOADS) return;
    const next = pendingQueueRef.current.shift();
    if (!next) return;

    activeCount.current += 1;
    updateItem(next.id, { status: "uploading", progress: 0, error: undefined });

    uploadSingleFile(token, next.file, (fraction) => updateItem(next.id, { progress: fraction }))
      .then((result) => {
        updateItem(next.id, { status: "done", progress: 1, mediaId: result.mediaId });
      })
      .catch((err: unknown) => {
        updateItem(next.id, {
          status: "error",
          error: err instanceof Error ? err.message : "Échec de l'envoi",
        });
      })
      .finally(() => {
        activeCount.current -= 1;
        runNext();
      });
  }, [token, updateItem]);

  const startWorkers = useCallback(() => {
    for (let i = 0; i < MAX_CONCURRENT_CLIENT_UPLOADS; i++) runNext();
  }, [runNext]);

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      // Rejected up front, never even queued: instant, clear feedback
      // instead of streaming most of an oversized file before the server
      // (the actual authority) rejects it.
      const accepted = fileArray.slice(0, maxFilesPerUpload);
      const rejectedForCount = fileArray.slice(maxFilesPerUpload);

      const newItems: QueueItem[] = accepted.map((file) => {
        const ext = extensionOf(file.name);
        const invalidExt = !ALLOWED_EXTENSIONS_SET.has(ext);
        const tooLarge = file.size > maxFileSizeBytes;
        return {
          id: makeId(),
          file,
          status: invalidExt || tooLarge ? "error" : "pending",
          progress: 0,
          error: invalidExt
            ? `Format non autorisé (.${ext || "?"})`
            : tooLarge
              ? `Fichier trop volumineux (max ${formatBytes(maxFileSizeBytes)})`
              : undefined,
        };
      });

      const rejectedItems: QueueItem[] = rejectedForCount.map((file) => ({
        id: makeId(),
        file,
        status: "error",
        progress: 0,
        error: `Trop de fichiers sélectionnés à la fois (max ${maxFilesPerUpload})`,
      }));

      setItems((prev) => [...prev, ...newItems, ...rejectedItems]);
      for (const item of newItems) {
        if (item.status === "pending") pendingQueueRef.current.push({ id: item.id, file: item.file });
      }
      startWorkers();
    },
    [startWorkers, maxFileSizeBytes, maxFilesPerUpload],
  );

  const retry = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((it) => it.id === id);
      if (!item) return;
      updateItem(id, { status: "pending", error: undefined, progress: 0 });
      pendingQueueRef.current.push({ id: item.id, file: item.file });
      startWorkers();
    },
    [startWorkers, updateItem],
  );

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
    pendingQueueRef.current = pendingQueueRef.current.filter((queued) => queued.id !== id);
  }, []);

  const isUploading = useMemo(
    () => items.some((it) => it.status === "pending" || it.status === "uploading"),
    [items],
  );

  // Warn before the guest closes the tab, hits back, or navigates away
  // mid-upload — on a phone especially, backgrounding/closing the browser
  // can otherwise silently kill an in-progress transfer with no feedback.
  useEffect(() => {
    if (!isUploading) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isUploading]);

  return { items, addFiles, retry, remove, isUploading };
}
